import { createAuthClient } from "better-auth/react";
import { twoFactorClient } from "better-auth/client/plugins";
import { AI_RACER_NAME_REFUSED_REASON, AI_RESIDENT_NAME_REFUSED_REASON, AI_TRUCK_RACERS, isAiResidentName, normalizeBackupCode, normalizeTotpCode } from "@townsy/contracts";

/**
 * Better Auth browser client. Same origin: the API mounts the handler at `/api/auth/*`
 * (docs/build brief section 3), so cookies stay first-party and no token is ever stored here.
 * The two-factor plugin client (ADR-0039) adds `authClient.twoFactor.*`; the sign-in code step is handled by the
 * account page itself (no redirect option is set).
 */
export const authClient = createAuthClient({
  baseURL: typeof window === "undefined" ? "http://localhost:5173" : window.location.origin,
  basePath: "/api/auth",
  plugins: [twoFactorClient()],
});

/** Client-side hint only; the server validates with `validateDisplayName` (docs/03 section 1). */
export const DISPLAY_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{2,19}$/;

export function displayNameProblem(name: string): string | null {
  if (!DISPLAY_NAME_RE.test(name)) return "3–20 letters, digits or underscores, starting with a letter.";
  if (/^guest\d*$/i.test(name) || /^townsy/i.test(name)) return "That name is reserved.";
  if (/^(admin|administrator|moderator|mod|staff|support|system|official|owner|root|sysop)(_|\d|$)/i.test(name)) {
    return "That name is reserved.";
  }
  // The AI racers' names (ADR-0032); the server's check also catches leetspeak and repeated letters.
  const plain = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (AI_TRUCK_RACERS.some((r) => r.key === plain)) return AI_RACER_NAME_REFUSED_REASON;
  // The AI residents' first names (exactly, any case), as the server reserves them.
  if (isAiResidentName(name)) return AI_RESIDENT_NAME_REFUSED_REASON;
  return null;
}

export interface AuthResult {
  ok: boolean;
  message: string | null;
  /** Better Auth error code when there is one (e.g. "INVALID_TOKEN"). */
  code?: string | null;
  /** Sign-in: the password was right and the account asks for its two-step sign-in code next (ADR-0039). */
  twoFactor?: boolean;
}

/**
 * Plain messages for the two-step sign-in answers (Better Auth two-factor plugin codes, Townsy refusals and HTTP 429).
 * Unknown codes fall back to the server's message.
 */
export const TWO_FACTOR_MESSAGES: Readonly<Record<string, string>> = {
  INVALID_CODE: "That code is not right. Use the newest 6-digit code in your authenticator app (and check that your phone sets its clock automatically).",
  INVALID_BACKUP_CODE: "That backup code is not right, or it was already used. Each backup code works once.",
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: "Too many wrong codes for this sign-in. Enter your email and password again to get a new try.",
  ACCOUNT_TEMPORARILY_LOCKED: "Too many wrong codes. For your safety, wait 15 minutes, then sign in again.",
  INVALID_TWO_FACTOR_COOKIE: "This sign-in timed out. Enter your email and password again.",
  TRUST_DEVICE_NOT_OFFERED: "Townsy asks for your code every time you sign in.",
  INVALID_PASSWORD: "That password is not right.",
  TOTP_ALREADY_ENABLED: "Two-step sign-in is already on for this account.",
  ALREADY_SIGNED_IN: "You're already signed in.",
  RATE_LIMITED: "Too many tries. Wait a minute and try again.",
};

export function twoFactorErrorMessage(code: string | null | undefined, fallback: string): string {
  return (code && TWO_FACTOR_MESSAGES[code]) || fallback;
}

/** Verification links land here (ADR-0005 / HOMES_ROUND2_BRIEF: `/account?verified=1`). */
export const VERIFIED_CALLBACK_PATH = "/account?verified=1";
/** Password reset links land here with `?token=...` (or `?error=INVALID_TOKEN`). */
export const RESET_PASSWORD_PATH = "/reset-password";
/** Same limits as the server (Better Auth `minPasswordLength: 8`, default maximum 128). */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

/** Client-side check of a new password and its confirmation; the server validates again. */
export function newPasswordProblem(password: string, confirm: string): string | null {
  if (password.length < PASSWORD_MIN) return `Use at least ${PASSWORD_MIN} characters.`;
  if (password.length > PASSWORD_MAX) return `Use at most ${PASSWORD_MAX} characters.`;
  if (password !== confirm) return "The two passwords don't match.";
  return null;
}

function errorCode(error: unknown): string | null {
  if (error && typeof error === "object" && typeof (error as { code?: unknown }).code === "string") return (error as { code: string }).code;
  return null;
}

function describeAuthError(error: unknown): string {
  if (error && typeof error === "object") {
    const e = error as { message?: unknown; code?: unknown; status?: unknown; statusText?: unknown };
    if (typeof e.message === "string" && e.message) return e.message;
    if (typeof e.code === "string") return e.code.replace(/_/g, " ").toLowerCase();
    if (typeof e.statusText === "string" && e.statusText) return e.statusText;
  }
  return "Sign-in failed. Please try again.";
}

export async function signUpWithEmail(input: { displayName: string; email: string; password: string; ageConfirmed?: boolean }): Promise<AuthResult> {
  try {
    const body = {
      name: input.displayName,
      email: input.email,
      password: input.password,
      // Where the verification link lands (outside local development the server emails it).
      callbackURL: VERIFIED_CALLBACK_PATH,
      // ADR-0047: the 18+ checkbox; the server refuses a sign-up without it when the audience is adults only.
      ...(input.ageConfirmed ? { ageConfirmed: true } : {}),
    };
    const res = await authClient.signUp.email(body as Parameters<typeof authClient.signUp.email>[0]);
    if (res.error) return { ok: false, message: describeAuthError(res.error) };
    return { ok: true, message: null };
  } catch (err) {
    return { ok: false, message: describeAuthError(err) };
  }
}

export async function signInWithEmail(input: { email: string; password: string }): Promise<AuthResult> {
  try {
    const res = await authClient.signIn.email({ email: input.email, password: input.password });
    if (res.error) return { ok: false, message: describeAuthError(res.error) };
    // ADR-0039: no session yet; the account asks for its code (POST /two-factor/verify-totp or verify-backup-code).
    if (res.data && "twoFactorRedirect" in res.data && res.data.twoFactorRedirect) return { ok: true, message: null, twoFactor: true };
    return { ok: true, message: null };
  } catch (err) {
    return { ok: false, message: describeAuthError(err) };
  }
}

function statusCode(error: unknown): number | null {
  return error && typeof error === "object" && typeof (error as { status?: unknown }).status === "number" ? (error as { status: number }).status : null;
}

/** A failed two-factor call as a plain message (429 has no Better Auth code). */
function twoFactorFailure(error: unknown, fallback: string): AuthResult {
  const code = statusCode(error) === 429 ? "RATE_LIMITED" : errorCode(error);
  return { ok: false, code, message: twoFactorErrorMessage(code, describeAuthError(error) || fallback) };
}

/** Second step of a two-step sign-in: the 6-digit code from the authenticator app. */
export async function verifySignInCode(code: string): Promise<AuthResult> {
  try {
    const res = await authClient.twoFactor.verifyTotp({ code: normalizeTotpCode(code) });
    if (res.error) return twoFactorFailure(res.error, "The code could not be checked.");
    return { ok: true, message: null };
  } catch (err) {
    return twoFactorFailure(err, "The code could not be checked.");
  }
}

/** Second step of a two-step sign-in with one of the backup codes (each works once). */
export async function verifySignInBackupCode(code: string): Promise<AuthResult> {
  try {
    const res = await authClient.twoFactor.verifyBackupCode({ code: normalizeBackupCode(code) });
    if (res.error) return twoFactorFailure(res.error, "The backup code could not be checked.");
    return { ok: true, message: null };
  } catch (err) {
    return twoFactorFailure(err, "The backup code could not be checked.");
  }
}

export type TwoFactorSetupStart = { ok: true; totpUri: string; backupCodes: string[] } | { ok: false; message: string; code: string | null };

/** Set-up step 1 (needs the password): a new authenticator secret (as an otpauth URI) and the backup codes. */
export async function startTwoFactorSetup(password: string): Promise<TwoFactorSetupStart> {
  try {
    const res = await authClient.twoFactor.enable({ password });
    if (res.error || !res.data || !("totpURI" in res.data) || !res.data.totpURI) {
      const failed = twoFactorFailure(res.error, "Two-step sign-in could not be set up.");
      return { ok: false, message: failed.message ?? "Two-step sign-in could not be set up.", code: failed.code ?? null };
    }
    return { ok: true, totpUri: res.data.totpURI, backupCodes: res.data.backupCodes ?? [] };
  } catch (err) {
    const failed = twoFactorFailure(err, "Two-step sign-in could not be set up.");
    return { ok: false, message: failed.message ?? "Two-step sign-in could not be set up.", code: failed.code ?? null };
  }
}

/** Set-up step 2: the first code from the app turns two-step sign-in on (the server replaces this session). */
export async function confirmTwoFactorSetup(code: string): Promise<AuthResult> {
  return verifySignInCode(code);
}

export async function signOut(): Promise<AuthResult> {
  try {
    const res = await authClient.signOut();
    if (res.error) return { ok: false, message: describeAuthError(res.error) };
    return { ok: true, message: null };
  } catch (err) {
    return { ok: false, message: describeAuthError(err) };
  }
}

/** Send a fresh verification link to the signed-in resident's address. */
export async function resendVerificationEmail(email: string): Promise<AuthResult> {
  try {
    const res = await authClient.sendVerificationEmail({ email, callbackURL: VERIFIED_CALLBACK_PATH });
    if (res.error) return { ok: false, message: describeAuthError(res.error), code: errorCode(res.error) };
    return { ok: true, message: null };
  } catch (err) {
    return { ok: false, message: describeAuthError(err), code: errorCode(err) };
  }
}

/**
 * Ask for a password reset link. The answer never reveals whether an account exists for the
 * address (the UI shows the same neutral confirmation either way).
 */
export async function requestPasswordReset(email: string): Promise<AuthResult> {
  try {
    const res = await authClient.requestPasswordReset({ email, redirectTo: RESET_PASSWORD_PATH });
    if (res.error) return { ok: false, message: describeAuthError(res.error), code: errorCode(res.error) };
    return { ok: true, message: null };
  } catch (err) {
    return { ok: false, message: describeAuthError(err), code: errorCode(err) };
  }
}

/** Set a new password with the token from the reset link. */
export async function resetPassword(token: string, newPassword: string): Promise<AuthResult> {
  try {
    const res = await authClient.resetPassword({ token, newPassword });
    if (res.error) return { ok: false, message: describeAuthError(res.error), code: errorCode(res.error) };
    return { ok: true, message: null };
  } catch (err) {
    return { ok: false, message: describeAuthError(err), code: errorCode(err) };
  }
}
